import { useState } from "react";
import { post } from "../api";
import type { Assignable, ReportAction, Severity } from "../api";

const SEVERITIES: Severity[] = ["critical", "high", "medium", "low"];

/** The agent's three-level priority maps straight onto the tracker's
    middle three; "critical" is a judgement only a person makes. */
function seedSeverity(priority: string): Severity {
  return (SEVERITIES as string[]).includes(priority) ? (priority as Severity) : "medium";
}

/**
 * Turning a suggestion into something somebody owns.
 *
 * The wording is editable before it becomes a row: the point of the button
 * is that a person signs off on what they are taking on, not that the
 * model's text is copied verbatim into a commitment.
 */
export default function TrackForm({
  action,
  users,
  customer,
  reportId,
  onDone,
}: {
  action: ReportAction;
  users: Assignable[];
  customer: string;
  reportId: string;
  onDone: () => void;
}) {
  const [severity, setSeverity] = useState<Severity>(seedSeverity(action.priority));
  const [assignee, setAssignee] = useState("");
  const [eta, setEta] = useState("");
  const [description, setDescription] = useState(action.detail);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await post("/api/actions", {
        customer,
        title: action.title,
        description,
        severity,
        assignee_user_id: assignee || null,
        eta: eta || null,
        source_report_id: reportId,
        source_action_id: action.id,
      });
      onDone();
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  };

  return (
    <form className="trackform" onSubmit={submit}>
      {error && <div className="error">{error}</div>}
      <div className="field">
        <label htmlFor={`desc-${action.id}`}>Description</label>
        <textarea
          id={`desc-${action.id}`}
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>
      <div className="row">
        <div className="field">
          <label>Severity</label>
          <select value={severity} onChange={(e) => setSeverity(e.target.value as Severity)}>
            {SEVERITIES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Assign to</label>
          <select value={assignee} onChange={(e) => setAssignee(e.target.value)}>
            <option value="">Nobody yet</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>ETA (optional)</label>
          <input type="date" value={eta} onChange={(e) => setEta(e.target.value)} />
        </div>
        <div className="field tight">
          <button className="primary" type="submit" disabled={busy}>
            {busy ? "Tracking…" : "Create item"}
          </button>
        </div>
      </div>
    </form>
  );
}
